import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { CheckCircle, Copy, ArrowLeft, ArrowRight, Loader2, Bitcoin } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLAN_DETAILS = {
  starter: { name: "Base", amount: 70, period: "1 Month" },
  pro:     { name: "Pro", amount: 110, period: "1 Month" },
};

const COIN_ICONS = {
  USDT: (
    <svg viewBox="0 0 32 32" width="32" height="32" fill="none">
      <circle cx="16" cy="16" r="16" fill="#26A17B"/>
      <path d="M17.922 17.383v-.002c-.11.008-.677.042-1.942.042-1.01 0-1.721-.03-1.971-.042v.003c-3.888-.171-6.79-.848-6.79-1.658 0-.809 2.902-1.486 6.79-1.66v2.644c.254.018.982.061 1.988.061 1.207 0 1.812-.05 1.925-.06v-2.643c3.88.173 6.775.85 6.775 1.658 0 .81-2.895 1.485-6.775 1.657zm0-3.59v-2.366h5.414V8.5H8.664v2.927h5.414v2.365c-4.4.202-7.707 1.074-7.707 2.122 0 1.048 3.307 1.919 7.707 2.12v7.594h3.843v-7.593c4.394-.202 7.694-1.073 7.694-2.12 0-1.049-3.3-1.92-7.694-2.122z" fill="white"/>
    </svg>
  ),
  BTC: <Bitcoin size={32} color="#F7931A" />,
};

export default function CryptoPayment() {
  const { plan: planParam } = useParams();
  const navigate = useNavigate();
  const plan = planParam?.toLowerCase() || "pro";
  const planInfo = PLAN_DETAILS[plan] || PLAN_DETAILS.pro;

  const [step, setStep] = useState(1); // 1=coin, 2=network, 3=address+QR, 4=submit
  const [wallets, setWallets] = useState({});
  const [selectedCoin, setSelectedCoin] = useState(null);
  const [selectedNetwork, setSelectedNetwork] = useState(null);
  const [txHash, setTxHash] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Please log in to continue.");
      navigate("/");
      return;
    }
    axios.get(`${API}/payments/wallets`).then((res) => {
      setWallets(res.data.wallets || {});
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [navigate]);

  const walletAddress = selectedCoin && selectedNetwork
    ? wallets[selectedCoin]?.[selectedNetwork]
    : null;

  const copyAddress = () => {
    if (walletAddress) {
      navigator.clipboard.writeText(walletAddress);
      setCopied(true);
      toast.success("Address copied!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleSubmit = async () => {
    if (!txHash.trim()) {
      toast.error("Please paste your transaction hash.");
      return;
    }
    setSubmitting(true);
    const token = localStorage.getItem("token");
    try {
      await axios.post(
        `${API}/payments/submit`,
        {
          plan,
          coin: selectedCoin,
          network: selectedNetwork,
          tx_hash: txHash.trim(),
          amount_sent: planInfo.amount,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Payment submitted! Awaiting owner confirmation.");
      navigate("/payment/pending");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit payment.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-[#00E676] animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />
      <div className="pt-28 pb-20 px-4 md:px-8">
        <div className="max-w-xl mx-auto">

          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold mb-2">Pay with Crypto</h1>
            <p className="text-[#A1A1AA]">
              Plan: <span className="text-[#00E676] font-semibold">{planInfo.name}</span> &nbsp;|&nbsp;
              Amount: <span className="text-white font-semibold">${planInfo.amount}</span>
            </p>
          </div>

          {/* Step Indicators */}
          <div className="flex items-center justify-center gap-2 mb-8">
            {[1, 2, 3, 4].map((s) => (
              <div key={s} className="flex items-center gap-2">
                <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold transition-all ${
                  step > s ? "bg-[#00E676] text-black" :
                  step === s ? "bg-[#00E676]/20 border-2 border-[#00E676] text-[#00E676]" :
                  "bg-[#1C1C1E] text-[#52525B]"
                }`}>
                  {step > s ? <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> : s}
                </div>
                {s < 4 && <div className={`w-6 sm:w-8 h-0.5 ${step > s ? "bg-[#00E676]" : "bg-[#27272A]"}`} />}
              </div>
            ))}
          </div>

          {/* Step 1: Choose Coin */}
          {step === 1 && (
            <div className="bg-[#111111] border border-[#27272A] rounded-2xl p-6 space-y-4">
              <h2 className="text-lg font-semibold text-center mb-6">Select Cryptocurrency</h2>
              <div className="grid grid-cols-2 gap-4">
                {Object.keys(wallets).map((coin) => (
                  <button
                    key={coin}
                    onClick={() => { setSelectedCoin(coin); setStep(2); }}
                    className="flex flex-col items-center gap-3 p-6 rounded-xl border border-[#27272A] hover:border-[#00E676] hover:bg-[#00E676]/5 transition-all group"
                  >
                    {COIN_ICONS[coin] || <div className="w-8 h-8 rounded-full bg-[#27272A]" />}
                    <span className="font-semibold group-hover:text-[#00E676]">{coin}</span>
                    <span className="text-xs text-[#52525B]">{Object.keys(wallets[coin]).length} network{Object.keys(wallets[coin]).length > 1 ? "s" : ""}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Choose Network */}
          {step === 2 && selectedCoin && (
            <div className="bg-[#111111] border border-[#27272A] rounded-2xl p-6 space-y-4">
              <button onClick={() => setStep(1)} className="flex items-center gap-1 text-[#A1A1AA] hover:text-white text-sm mb-2">
                <ArrowLeft size={14} /> Back
              </button>
              <h2 className="text-lg font-semibold text-center mb-4">Select Network for {selectedCoin}</h2>
              <div className="space-y-3">
                {Object.keys(wallets[selectedCoin]).map((network) => (
                  <button
                    key={network}
                    onClick={() => { setSelectedNetwork(network); setStep(3); }}
                    className="w-full flex items-center justify-between p-4 rounded-xl border border-[#27272A] hover:border-[#00E676] hover:bg-[#00E676]/5 transition-all group text-left"
                  >
                    <span className="font-medium group-hover:text-[#00E676]">{network}</span>
                    <ArrowRight size={16} className="text-[#52525B] group-hover:text-[#00E676]" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Wallet Address + QR */}
          {step === 3 && walletAddress && (
            <div className="bg-[#111111] border border-[#27272A] rounded-2xl p-6 space-y-5">
              <button onClick={() => setStep(2)} className="flex items-center gap-1 text-[#A1A1AA] hover:text-white text-sm">
                <ArrowLeft size={14} /> Back
              </button>

              <div className="text-center">
                <h2 className="text-lg font-semibold mb-1">Send {selectedCoin}</h2>
                <p className="text-sm text-[#A1A1AA]">Network: <span className="text-white">{selectedNetwork}</span></p>
              </div>

              {/* QR Code */}
              <div className="flex justify-center">
                <div className="p-3 sm:p-4 bg-white rounded-xl inline-block">
                  <QRCodeSVG value={walletAddress} size={150} className="sm:w-[180px] sm:h-[180px]" />
                </div>
              </div>

              {/* Wallet Address */}
              <div className="bg-[#0A0A0A] border border-[#27272A] rounded-xl p-4">
                <p className="text-xs text-[#A1A1AA] mb-1">Send exactly <span className="text-[#00E676] font-semibold">${planInfo.amount} USD</span> worth of {selectedCoin} to:</p>
                <div className="flex items-center gap-2 mt-2">
                  <p className="font-mono text-sm text-white flex-1 break-all">{walletAddress}</p>
                  <button
                    onClick={copyAddress}
                    className="shrink-0 p-2 rounded-lg bg-[#1C1C1E] hover:bg-[#27272A] transition-colors"
                  >
                    {copied ? <CheckCircle size={16} className="text-[#00E676]" /> : <Copy size={16} className="text-[#A1A1AA]" />}
                  </button>
                </div>
              </div>

              <div className="bg-amber-900/20 border border-amber-700/40 rounded-xl p-4 text-sm text-amber-200 space-y-1">
                <p>⚠️ <strong>Important:</strong></p>
                <ul className="list-disc list-inside space-y-1 text-amber-300/80">
                  <li>Send only {selectedCoin} on the <strong>{selectedNetwork}</strong> network</li>
                  <li>Sending on incorrect network will result in loss of funds</li>
                  <li>After sending, copy the transaction hash and click Continue</li>
                </ul>
              </div>

              <Button
                onClick={() => setStep(4)}
                className="w-full bg-[#00E676] hover:bg-[#00C853] text-black font-semibold py-3"
              >
                I've Sent the Payment <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          )}

          {/* Step 4: Submit TxHash */}
          {step === 4 && (
            <div className="bg-[#111111] border border-[#27272A] rounded-2xl p-6 space-y-5">
              <button onClick={() => setStep(3)} className="flex items-center gap-1 text-[#A1A1AA] hover:text-white text-sm">
                <ArrowLeft size={14} /> Back
              </button>
              <div className="text-center">
                <h2 className="text-lg font-semibold mb-2">Submit Transaction Hash</h2>
                <p className="text-sm text-[#A1A1AA]">Paste your transaction hash so we can verify your payment.</p>
              </div>

              <div className="space-y-2">
                <label className="text-sm text-[#A1A1AA] block">Transaction Hash / TxID</label>
                <input
                  type="text"
                  value={txHash}
                  onChange={(e) => setTxHash(e.target.value)}
                  placeholder="0x... or TxID..."
                  className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-xl px-4 py-3 text-white font-mono text-sm placeholder:text-[#52525B] focus:outline-none focus:border-[#00E676] transition-colors"
                />
              </div>

              {/* Summary */}
              <div className="bg-[#0A0A0A] border border-[#27272A] rounded-xl p-4 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-[#A1A1AA]">Plan</span><span>{planInfo.name}</span></div>
                <div className="flex justify-between"><span className="text-[#A1A1AA]">Amount</span><span className="text-[#00E676] font-semibold">${planInfo.amount}</span></div>
                <div className="flex justify-between"><span className="text-[#A1A1AA]">Coin</span><span>{selectedCoin}</span></div>
                <div className="flex justify-between"><span className="text-[#A1A1AA]">Network</span><span>{selectedNetwork}</span></div>
              </div>

              <Button
                onClick={handleSubmit}
                disabled={submitting || !txHash.trim()}
                className="w-full bg-[#00E676] hover:bg-[#00C853] text-black font-semibold py-3 disabled:opacity-50"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Confirm & Submit Payment
              </Button>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
